import {
  FirestoreDataConverter,
  DocumentSnapshot,
  SnapshotOptions,
  WithFieldValue,
  Timestamp,
} from 'firebase/firestore'
import { DealApproval, VehicleDecision, StageDecision, DealApprovalStageType } from '../deals'

function convertTimestamp(timestamp: any): string {
  if (!timestamp) return new Date().toISOString()
  if (timestamp instanceof Timestamp) return timestamp.toDate().toISOString()
  if (typeof timestamp === 'string') return timestamp
  return new Date().toISOString()
}

/** Normalize a stage decision from Firestore (convert at timestamp). */
function normalizeStageDecision(entry: any): StageDecision | undefined {
  if (!entry || typeof entry !== 'object' || !entry.user_id || !entry.status) return undefined
  return {
    user_id: entry.user_id,
    status: entry.status,
    note: entry.note,
    at: entry.at ? convertTimestamp(entry.at) : undefined,
  }
}

/** Migrate old vehicle decision shape to new shape (availability/condition/title/final with StageDecision). */
function migrateVehicleDecision(entry: any): VehicleDecision {
  // New shape: at least one of availability/location/condition/title/final is an object with user_id
  const locOrAvail = entry.availability ?? entry.location
  const hasNewShape =
    (locOrAvail && typeof locOrAvail === 'object' && 'user_id' in locOrAvail) ||
    (entry.condition && typeof entry.condition === 'object' && 'user_id' in entry.condition) ||
    (entry.title && typeof entry.title === 'object' && 'user_id' in entry.title) ||
    (entry.final && typeof entry.final === 'object' && 'user_id' in entry.final)
  if (hasNewShape) {
    return {
      availability: normalizeStageDecision(locOrAvail),
      condition: normalizeStageDecision(entry.condition),
      title: normalizeStageDecision(entry.title),
      final: normalizeStageDecision(entry.final),
    }
  }
  // Old shape: location_approved_at, condition_approved_at, final_approved_at (location → availability; no title)
  const out: VehicleDecision = {}
  const stages: DealApprovalStageType[] = ['availability', 'condition', 'final']
  const atKeys = ['location_approved_at', 'condition_approved_at', 'final_approved_at']
  const byKeys = ['location_approved_by', 'condition_approved_by', 'final_approved_by']
  const rejectedStageRaw = entry.rejected_at_stage
  const legacyToNew: Record<string, DealApprovalStageType> = { location: 'availability', condition: 'condition', final: 'final' }
  const rejectedStage: DealApprovalStageType | undefined =
    rejectedStageRaw && legacyToNew[rejectedStageRaw]
      ? legacyToNew[rejectedStageRaw]
      : rejectedStageRaw && stages.includes(rejectedStageRaw)
        ? (rejectedStageRaw as DealApprovalStageType)
        : undefined
  const conditionalStageRaw = entry.conditionally_approved_at_stage
  const conditionalStage = conditionalStageRaw && legacyToNew[conditionalStageRaw]
    ? legacyToNew[conditionalStageRaw]
    : conditionalStageRaw && stages.includes(conditionalStageRaw)
      ? (conditionalStageRaw as DealApprovalStageType)
      : undefined

  for (let i = 0; i < 3; i++) {
    const stage = stages[i]
    const at = entry[atKeys[i]]
    const by = entry[byKeys[i]]
    if (at && by) {
      if (rejectedStage === stage) {
        out[stage] = {
          user_id: entry.rejected_by || by,
          status: 'rejected',
          note: entry.note,
          at: convertTimestamp(entry.rejected_at || at),
        }
      } else if (conditionalStage === stage) {
        out[stage] = {
          user_id: by,
          status: 'conditionally_approved',
          note: entry.note,
          at: convertTimestamp(at),
        }
      } else {
        out[stage] = { user_id: by, status: 'approved', at: convertTimestamp(at) }
      }
    }
  }
  if (rejectedStage && !out[rejectedStage]) {
    out[rejectedStage] = {
      user_id: entry.rejected_by || '',
      status: 'rejected',
      note: entry.note,
      at: entry.rejected_at ? convertTimestamp(entry.rejected_at) : undefined,
    }
  }
  return out
}

export const dealApprovalConverter: FirestoreDataConverter<DealApproval> = {
  toFirestore(deal: WithFieldValue<DealApproval>) {
    const { id, ...data } = deal as any
    const cleanedData: any = {}
    Object.keys(data).forEach(key => {
      const value = data[key]
      if (value !== undefined) {
        if (key === 'vehicle_decisions' && value && typeof value === 'object') {
          cleanedData[key] = value
        } else {
          cleanedData[key] = value
        }
      }
    })
    return cleanedData
  },

  fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions): DealApproval {
    const data = snapshot.data(options)

    if (!data) {
      return {
        id: snapshot.id,
        created_by: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        vehicle_ids: [],
      }
    }

    const vd = data.vehicle_decisions
    let vehicle_decisions: Record<string, VehicleDecision> | undefined
    if (vd && typeof vd === 'object') {
      vehicle_decisions = {}
      for (const [k, v] of Object.entries(vd)) {
        if (v && typeof v === 'object') {
          vehicle_decisions[k] = migrateVehicleDecision(v as any)
        }
      }
      if (Object.keys(vehicle_decisions).length === 0) vehicle_decisions = undefined
    }

    return {
      id: snapshot.id,
      created_by: data.created_by || '',
      created_by_name: data.created_by_name,
      created_at: convertTimestamp(data.created_at),
      updated_at: convertTimestamp(data.updated_at),
      vehicle_ids: Array.isArray(data.vehicle_ids) ? data.vehicle_ids : [],
      vehicle_summaries: Array.isArray(data.vehicle_summaries) ? data.vehicle_summaries : undefined,
      vehicles: data.vehicles,
      notes: data.notes,
      vehicle_decisions,
    }
  },
}
